import React, { useRef, useState } from 'react'
import ReactDatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { DatePickerProps } from './DatePicker.types'
import { useTranslation } from 'react-i18next'
import { formatDateOnlyLocal } from '../../utils/datetimeFormatUtils'

const DatePicker: React.FC<DatePickerProps> = ({
  selectedDate,
  setSelectedDate,
  minDate,
  maxDate,
  popperPlacement: popperPlacementProp,
}) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // Custom label logic
  const formatDate = (date: Date) => {
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)
    const d = new Date(date)
    d.setHours(0, 0, 0, 0)
    today.setHours(0, 0, 0, 0)
    yesterday.setHours(0, 0, 0, 0)
    if (d.getTime() === today.getTime()) return t('common.today', 'Today')
    if (d.getTime() === yesterday.getTime())
      return t('common.yesterday', 'Yesterday')
    return d.toLocaleDateString('en-GB') // dd/mm/yyyy
  }

  return (
    <div
      className="date-picker-trigger relative px-4 py-2 w-fit transition border"
      style={{
        borderRadius: 'var(--custom-radius-md)',
      }}
    >
      <input
        ref={inputRef}
        value={formatDate(new Date(selectedDate))}
        readOnly
        onClick={() => setOpen(true)}
        className="bg-transparent outline-none cursor-pointer w-full pr-8"
        style={{ color: 'inherit', width: '100%' }}
        title={formatDate(new Date(selectedDate))}
        placeholder={formatDate(new Date(selectedDate))}
      />
      <ReactDatePicker
        selected={new Date(selectedDate)}
        onChange={(date: Date | null) => {
          if (date) {
            setSelectedDate(formatDateOnlyLocal(date))
            setOpen(false)
          }
        }}
        open={open}
        onClickOutside={() => setOpen(false)}
        onSelect={() => setOpen(false)}
        minDate={minDate}
        maxDate={maxDate}
        dateFormat="dd/MM/yyyy"
        calendarClassName="custom-calendar"
        popperPlacement={popperPlacementProp ?? 'bottom-end'}
        popperClassName="!z-50"
        // Hide the default input
        customInput={<div style={{ display: 'none' }} />}
        dayClassName={(date) => {
          if (minDate && date < new Date(minDate)) return 'disabled-date'
          if (maxDate && date > new Date(maxDate)) return 'disabled-date'
          return ''
        }}
      />
      {/* Right calendar icon */}
      <span className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M8 7V3M16 7V3M3 11h18M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      </span>
    </div>
  )
}

export default DatePicker
